import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentUser, signOut, fetchAuthSession } from 'aws-amplify/auth';
import { toast } from 'react-toastify';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper function to decode JWT
function decodeJwt(token) {
  try {
    console.log('Decoding JWT token:', token.substring(0, 50) + '...');
    const parts = token.split('.');
    console.log('JWT parts count:', parts.length);
    
    if (parts.length !== 3) {
      console.error('Invalid JWT format - expected 3 parts');
      return {};
    }
    
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    
    const payload = JSON.parse(jsonPayload);
    console.log('Successfully decoded JWT payload:', payload);
    return payload;
  } catch (error) {
    console.error('Error decoding JWT:', error);
    console.error('Token that failed to decode:', token);
    return {};
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      
      console.log('Current user:', currentUser);
      console.log('Session:', session);
      console.log('Session tokens:', session?.tokens);
      
      // Extract user info from currentUser.attributes if available
      const attributes = currentUser.attributes || {};
      
      // Get Google ID from JWT token
      let googleId = currentUser.userId; // fallback to Cognito ID
      if (session?.tokens?.idToken) {
        console.log('ID Token exists, decoding...');
        const idPayload = decodeJwt(session.tokens.idToken.toString());
        console.log('Decoded JWT payload:', idPayload);
        console.log('JWT sub field:', idPayload.sub);
        // Use the full Google ID format (e.g., "google_103332964364447799610")
        googleId = idPayload.sub || currentUser.userId;
        console.log('Using Google ID:', googleId);
      } else {
        console.log('No ID token found in session');
      }
      
      setUser({
        id: googleId, // Use Google ID as primary ID
        cognitoId: currentUser.userId, // Keep Cognito ID for reference
        username: currentUser.username,
        email: attributes.email,
        name: attributes.name || attributes.given_name || attributes.email?.split('@')[0] || currentUser.username,
        picture: attributes.picture
      });
      
      console.log('Final user object:', {
        id: googleId,
        cognitoId: currentUser.userId,
        username: currentUser.username,
        email: attributes.email
      });
    } catch (error) {
      console.log('No authenticated user:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setUser(null);
      toast.success('Signed out successfully');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Failed to sign out');
    }
  };

  const value = {
    user,
    loading,
    signOut: handleSignOut,
    checkAuthState,
    refreshUser: checkAuthState
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 